import { useRef, useCallback } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { amlTraceHistorySelectors } from '@/redux/selectors';
import { amlTraceHistoryActions } from '@/redux/actions';
import { AmlTraceListRequest } from '@/api';
import { HistoryEmpty, Button } from '@/components';
import TraceHistoryItem from '../TraceHistoryItem/TraceHistoryItem';
import { useIsElementSticky } from '@/hooks/useIsElementSticky';

import styles from './TraceHistoryList.module.sass';

const TraceHistoryList = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const headerRef = useRef<HTMLDivElement>(null);
  const isSticky = useIsElementSticky(headerRef);

  const { data: traceHistory, loading } = useAppSelector(
    amlTraceHistorySelectors.getTraceHistorySlice,
  );

  const items = traceHistory?.items || [];
  const isShowMoreAvailable =
    !!traceHistory && traceHistory.totalCount - traceHistory.items.length > 0;

  const showMoreHandler = useCallback(() => {
    if (traceHistory) {
      if (traceHistory.totalCount - traceHistory.items.length > 0) {
        const currentPage = Math.ceil(
          traceHistory.items.length / traceHistory.pageLimit,
        );

        const payload: AmlTraceListRequest = {
          // backend counts pages from 0
          page: currentPage,
        };

        dispatch(amlTraceHistoryActions.fetchTraceHistory(payload));
      }
    }
  }, [dispatch, traceHistory]);

  if (items.length === 0) {
    return (
      <HistoryEmpty
        title={t('translation.empty_aml_history_title')}
        desc={t('translation.empty_aml_history_description')}
      />
    );
  }

  const classes = cx(styles.list, {
    [styles['is-sticky']]: isSticky,
  });

  return (
    <div className={classes}>
      <div ref={headerRef} className={styles.header}>
        <div className={styles.iconCol}></div>
        <div className={styles.addressCol}>
          {t('translation.amlhistory_table_name')}
        </div>

        <div className={styles.datetimeCol}>
          {t('translation.last_checked')}
        </div>

        <div className={styles.statusCol}>
          {t('translation.monitoring_table_status')}
        </div>

        <div className={styles.actionsCol}>{t('translation.actions')}</div>
      </div>

      <div className={styles.items}>
        {items.map((item) => (
          <TraceHistoryItem key={item.uuid} item={item} />
        ))}

        {isShowMoreAvailable && (
          <div className={styles.show}>
            <Button
              size="m"
              onClick={showMoreHandler}
              disabled={loading}
              nowrap
            >
              {t('translation.next_page_btn')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TraceHistoryList;
